import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { adminAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Loader2, CheckCircle, XCircle, Users, UserCheck, ArrowLeft, Shield, UserPlus, Eye, EyeOff, Briefcase } from 'lucide-react';

const AVIDA_LOGO = "https://customer-assets.emergentagent.com/job_packet-digitizer/artifacts/f0t7kezs_Avida%20Logo%20Full%20Color.png";

export default function AdminPage() {
  const [pendingUsers, setPendingUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pending');
  const [newUserName, setNewUserName] = useState('');
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserPassword, setNewUserPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!user?.is_admin) {
      toast.error('Admin access required');
      navigate('/clients');
      return;
    }
    loadUsers();
  }, [user, navigate]);

  const loadUsers = async () => {
    setIsLoading(true);
    try {
      const [pending, all] = await Promise.all([
        adminAPI.getPendingUsers(),
        adminAPI.getAllUsers()
      ]);
      setPendingUsers(pending);
      setAllUsers(all);
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setIsLoading(false);
    }
  };

  const handleApprove = async (userId, userName) => {
    try {
      await adminAPI.approveUser(userId);
      toast.success(`${userName} has been approved`);
      loadUsers();
    } catch (error) {
      toast.error('Failed to approve user');
    }
  };

  const handleReject = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to reject and remove ${userName}?`)) return;
    try {
      await adminAPI.rejectUser(userId);
      toast.success(`${userName} has been rejected`);
      loadUsers();
    } catch (error) {
      toast.error('Failed to reject user');
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    if (!newUserName || !newUserEmail || !newUserPassword) {
      toast.error('Please fill in all fields');
      return;
    }
    if (newUserPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setIsCreating(true);
    try {
      await adminAPI.createUser(newUserName, newUserEmail, newUserPassword);
      toast.success(`User ${newUserName} created successfully`);
      setNewUserName('');
      setNewUserEmail('');
      setNewUserPassword('');
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    } finally {
      setIsCreating(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-amber-100">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src={AVIDA_LOGO} alt="Avida" className="h-12" />
            <div>
              <h1 className="text-xl font-bold text-blue-900">Admin Panel</h1>
              <p className="text-sm text-blue-600">User Management</p>
            </div>
          </div>
          <Button 
            variant="outline" 
            onClick={() => navigate('/clients')}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to App
          </Button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* HR Dashboard Link */}
        <Card 
          className="mb-8 p-6 bg-gradient-to-r from-[#0A368F] to-[#1E50A0] text-white cursor-pointer hover:shadow-lg transition-shadow"
          onClick={() => navigate('/admin/hr')}
          data-testid="hr-dashboard-link"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-white/20 rounded-xl">
                <Briefcase className="w-8 h-8" />
              </div>
              <div>
                <h2 className="text-xl font-semibold">HR Dashboard</h2>
                <p className="text-blue-200">View and manage employment applications & background checks</p>
              </div>
            </div>
            <Button className="bg-[#FBD704] text-[#0A368F] hover:bg-[#FCE44A]">
              Open Dashboard
            </Button>
          </div>
        </Card>
        
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <Card className="bg-amber-50 border-amber-200">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-amber-100 rounded-full">
                  <Users className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-amber-900">{allUsers.length}</p>
                  <p className="text-sm text-amber-700">Total Users</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-orange-50 border-orange-200">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-orange-100 rounded-full">
                  <Loader2 className="w-6 h-6 text-orange-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-orange-900">{pendingUsers.length}</p>
                  <p className="text-sm text-orange-700">Pending Approval</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-green-50 border-green-200">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-green-100 rounded-full">
                  <UserCheck className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-green-900">
                    {allUsers.filter(u => u.is_approved).length}
                  </p>
                  <p className="text-sm text-green-700">Approved Users</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <Button
            variant={activeTab === 'create' ? 'default' : 'outline'}
            onClick={() => setActiveTab('create')}
            className={activeTab === 'create' ? 'bg-blue-600' : ''}
          >
            <UserPlus className="w-4 h-4 mr-1" />
            Create User
          </Button>
          <Button
            variant={activeTab === 'pending' ? 'default' : 'outline'}
            onClick={() => setActiveTab('pending')}
            className={activeTab === 'pending' ? 'bg-blue-600' : ''}
          >
            Pending Approval ({pendingUsers.length})
          </Button>
          <Button
            variant={activeTab === 'all' ? 'default' : 'outline'}
            onClick={() => setActiveTab('all')}
            className={activeTab === 'all' ? 'bg-blue-600' : ''}
          >
            All Users ({allUsers.length})
          </Button>
        </div>

        {/* Create User */}
        {activeTab === 'create' && (
          <Card>
            <CardHeader>
              <CardTitle className="text-blue-900 flex items-center gap-2">
                <UserPlus className="w-5 h-5" />
                Create New User
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleCreateUser} className="space-y-4 max-w-md">
                <div className="space-y-2">
                  <Label htmlFor="new-user-name">Full Name</Label>
                  <Input
                    id="new-user-name"
                    type="text"
                    placeholder="John Smith"
                    value={newUserName}
                    onChange={(e) => setNewUserName(e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="new-user-email">Email</Label>
                  <Input
                    id="new-user-email"
                    type="email"
                    placeholder="john@avidahealth.org"
                    value={newUserEmail}
                    onChange={(e) => setNewUserEmail(e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="new-user-password">Password</Label>
                  <div className="relative">
                    <Input
                      id="new-user-password"
                      type={showPassword ? "text" : "password"}
                      placeholder="Min 6 characters"
                      value={newUserPassword}
                      onChange={(e) => setNewUserPassword(e.target.value)}
                      className="pr-10"
                      required
                      minLength={6}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    >
                      {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                </div>
                <Button 
                  type="submit" 
                  className="bg-blue-600 hover:bg-blue-700 w-full"
                  disabled={isCreating}
                >
                  {isCreating ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <UserPlus className="w-4 h-4 mr-2" />
                      Create User
                    </>
                  )}
                </Button>
              </form>
              <p className="text-sm text-gray-500 mt-4">
                Users created by admin are automatically approved and can login immediately.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Pending Users */}
        {activeTab === 'pending' && (
          <Card>
            <CardHeader>
              <CardTitle className="text-blue-900">Pending User Approvals</CardTitle>
            </CardHeader>
            <CardContent>
              {pendingUsers.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No pending approvals</p>
              ) : (
                <div className="space-y-4">
                  {pendingUsers.map((pendingUser) => (
                    <div 
                      key={pendingUser.id} 
                      className="flex items-center justify-between p-4 bg-amber-50 rounded-lg border border-amber-200"
                    >
                      <div>
                        <p className="font-semibold text-blue-900">{pendingUser.name}</p>
                        <p className="text-sm text-gray-600">{pendingUser.email}</p>
                        <p className="text-xs text-gray-400">
                          Registered: {new Date(pendingUser.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={() => handleApprove(pendingUser.id, pendingUser.name)}
                          className="bg-green-600 hover:bg-green-700"
                        >
                          <CheckCircle className="w-4 h-4 mr-1" />
                          Approve
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => handleReject(pendingUser.id, pendingUser.name)}
                        >
                          <XCircle className="w-4 h-4 mr-1" />
                          Reject
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* All Users */}
        {activeTab === 'all' && (
          <Card>
            <CardHeader>
              <CardTitle className="text-blue-900">All Users</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {allUsers.map((u) => (
                  <div 
                    key={u.id} 
                    className={`flex items-center justify-between p-4 rounded-lg border ${
                      u.is_admin ? 'bg-blue-50 border-blue-200' : 
                      u.is_approved ? 'bg-green-50 border-green-200' : 'bg-orange-50 border-orange-200'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      {u.is_admin && <Shield className="w-5 h-5 text-blue-600" />}
                      <div>
                        <p className="font-semibold text-blue-900">{u.name}</p>
                        <p className="text-sm text-gray-600">{u.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {u.is_admin && (
                        <span className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded-full">
                          Admin
                        </span>
                      )}
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        u.is_approved 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-orange-100 text-orange-700'
                      }`}>
                        {u.is_approved ? 'Approved' : 'Pending'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}
